import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import QrCodeImage from './QrCodeImage';

type Step = 'loading' | 'status' | 'setup' | 'error';

export default function MfaSettings() {
  const [step, setStep] = useState<Step>('loading');
  const [cognitoUser, setCognitoUser] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Auth.currentAuthenticatedUser({ bypassCache: true })
      .then(async (user) => {
        setCognitoUser(user);
        const attributes = await Auth.userAttributes(user);
        setEmail(attributes.find((attr) => attr.getName() === 'email')?.getValue() || user.username);
        const preferred = await Auth.getPreferredMFA(user, { bypassCache: true });
        setMfaEnabled(preferred === 'SOFTWARE_TOKEN_MFA');
        setStep('status');
      })
      .catch(() => setStep('error'));
  }, []);

  const startSetup = async () => {
    setIsLoading(true);
    setError('');
    try {
      const secret = await Auth.setupTOTP(cognitoUser);
      setSecretCode(secret);
      setStep('setup');
    } catch (err: any) {
      setError(err.message || 'Failed to start authenticator app setup');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyAndEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await Auth.verifyTotpToken(cognitoUser, totpCode);
      await Auth.setPreferredMFA(cognitoUser, 'TOTP');
      setMfaEnabled(true);
      setTotpCode('');
      setStep('status');
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const disableMfa = async () => {
    setIsLoading(true);
    setError('');
    try {
      await Auth.setPreferredMFA(cognitoUser, 'NOMFA');
      setMfaEnabled(false);
    } catch (err: any) {
      setError(err.message || 'Failed to disable two-factor authentication');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelSetup = () => {
    setStep('status');
    setSecretCode('');
    setTotpCode('');
    setError('');
  };

  const otpauthUri = cognitoUser && secretCode
    ? `otpauth://totp/PathboxAI:${encodeURIComponent(email)}?secret=${secretCode}&issuer=PathboxAI`
    : '';

  if (step === 'loading') {
    return (
      <div className="flex items-center gap-2 py-4">
        <span className="loading loading-spinner loading-sm"></span>
        <span>Loading security settings...</span>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="alert alert-error">
        <span>Unable to load security settings.</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {step === 'status' && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="font-medium">Authenticator app (TOTP)</p>
            <p className="text-sm text-base-content/70">
              {mfaEnabled
                ? 'Two-factor authentication is enabled for your account.'
                : 'Add an extra layer of security by requiring a code from an authenticator app at sign in.'}
            </p>
          </div>
          {mfaEnabled ? (
            <button
              className="btn btn-outline btn-error btn-sm"
              onClick={disableMfa}
              disabled={isLoading}
            >
              {isLoading ? 'Disabling...' : 'Disable'}
            </button>
          ) : (
            <button
              className="btn btn-primary btn-sm"
              onClick={startSetup}
              disabled={isLoading}
            >
              {isLoading ? 'Starting...' : 'Enable'}
            </button>
          )}
        </div>
      )}

      {step === 'setup' && (
        <form onSubmit={verifyAndEnable} className="space-y-4">
          <p className="text-sm text-base-content/70">
            Scan the code below with your authenticator app (e.g. Google Authenticator, Authy),
            or enter the key manually, then enter the 6-digit code it generates.
          </p>

          {otpauthUri && (
            <div className="flex justify-center">
              <QrCodeImage value={otpauthUri} />
            </div>
          )}

          <div className="form-control">
            <label className="label">
              <span className="label-text-alt">Manual entry key</span>
            </label>
            <code className="p-2 bg-base-200 rounded text-sm break-all">{secretCode}</code>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Authentication Code</span>
            </label>
            <input
              type="text"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value)}
              className="input input-bordered w-full text-center text-lg tracking-widest"
              placeholder="Enter 6-digit code"
              maxLength={6}
              required
              disabled={isLoading}
            />
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={cancelSetup}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={isLoading || totpCode.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Verify & Enable'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
